<template>
    <div class="channel-config">
      <div class="page-header">
        <h2 class="page-title">渠道配置</h2>
        <p class="page-desc">配置小程序等渠道参数</p>
      </div>
  
      <div class="config-container">
        <el-card class="config-card" shadow="hover">
          <template #header>
            <div class="card-header-title">
              <el-icon><Phone /></el-icon>
              <span>小程序配置</span>
            </div>
          </template>
  
          <el-form :model="form" ref="formRef" label-width="120px">
            <el-form-item label="AppID" required>
              <el-input v-model="form.appid" placeholder="请输入小程序AppID" />
            </el-form-item>
  
            <el-form-item label="AppSecret" required>
              <el-input v-model="form.appsecret" type="password" placeholder="请输入小程序AppSecret" show-password />
              <div class="form-tip">修改时需要重新输入，留空表示保持不变</div>
            </el-form-item>
  
            <el-form-item label="上传密钥">
              <el-input v-model="form.upload_key" type="textarea" :rows="4" placeholder="请输入小程序上传密钥（RSA私钥）" />
              <div class="form-tip">用于上传小程序代码，格式为 PEM 格式的私钥</div>
            </el-form-item>
  
            <el-form-item class="save-btn-item">
              <div class="btn-group">
                <el-button type="primary" @click="saveConfig" :loading="submitting" class="save-btn">
                  <el-icon><Check /></el-icon>保存配置
                </el-button>
                <el-button type="success" @click="openUploadCodeDialog" class="upload-btn">
                  <el-icon><Upload /></el-icon>上传小程序代码
                </el-button>
              </div>
            </el-form-item>
          </el-form>
        </el-card>
      </div>
  
      <!-- 上传代码弹窗 -->
      <el-dialog v-model="uploadCodeDialogVisible" title="上传小程序代码" width="650px" destroy-on-close>
        <el-form :model="uploadForm" label-width="120px">
          <el-form-item label="小程序AppID" required>
            <el-input v-model="uploadForm.appid" disabled />
            <div class="form-tip">从小程序配置中自动获取</div>
          </el-form-item>
          <el-form-item label="上传私钥" required>
            <el-input v-model="uploadForm.privateKeyContent" type="textarea" :rows="8" disabled />
            <div class="form-tip">从小程序配置中自动获取（upload_key）</div>
          </el-form-item>
          <el-form-item label="API域名" required>
            <el-input v-model="uploadForm.api_url" disabled />
            <div class="form-tip">自动使用系统配置的API域名</div>
          </el-form-item>
          <el-form-item label="商家ID" required>
            <el-input-number v-model="uploadForm.default_store_id" :disabled="true" :min="1" />
            <div class="form-tip">自动使用当前商家ID</div>
          </el-form-item>
        </el-form>
  
        <div v-if="uploading" class="upload-progress">
          <el-progress :percentage="uploadProgress" :status="uploadProgressStatus" />
          <div class="upload-log">
            <div v-for="(log, index) in uploadLogs" :key="index" class="log-line">{{ log }}</div>
          </div>
        </div>
  
        <template #footer>
          <el-button @click="uploadCodeDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="submitUploadCode" :loading="uploading" :disabled="uploading">
            {{ uploading ? '上传中...' : '开始上传' }}
          </el-button>
        </template>
      </el-dialog>
    </div>
  </template>
  
  <script>
  import { ref, reactive, onMounted } from 'vue'
  import { ElMessage, ElLoading } from 'element-plus'
  import Cookies from 'js-cookie'
  import axios from 'axios'
  import config from '@/config'
  import { Phone, Check, Upload } from '@element-plus/icons-vue'
  
  export default {
    name: 'ChannelConfig',
    components: { Phone, Check, Upload },
    setup() {
      const userInfo = JSON.parse(Cookies.get('user_info') || '{}')
      const storeId = ref(userInfo.id || 0)
      const agentId = ref(userInfo.agent_id || 0)
  
      const formRef = ref(null)
      const submitting = ref(false)
      const uploading = ref(false)
      const uploadProgress = ref(0)
      const uploadProgressStatus = ref('')
      const uploadLogs = ref([])
      const uploadCodeDialogVisible = ref(false)
  
      const form = reactive({
        appid: '',
        appsecret: '',
        upload_key: ''
      })
  
      const uploadForm = reactive({
        appid: '',
        privateKeyContent: '',
        api_url: config.BASE_URL,
        default_store_id: storeId.value
      })
  
      const fetchConfig = async () => {
        const loading = ElLoading.service({ text: '加载中...' })
        try {
          const res = await axios.get(`${config.BASE_URL}/shop/api/config_api.php`, {
            params: { agent_id: agentId.value, store_id: storeId.value, config_key: 'miniprogram' }
          })
          if (res.data.code === 200 && res.data.data) {
            form.appid = res.data.data.appid || ''
            form.upload_key = res.data.data.upload_key || ''
            form.appsecret = res.data.data.appsecret || ''
          }
        } catch (err) {
          console.error(err)
          ElMessage.error('获取配置失败')
        } finally {
          loading.close()
        }
      }
  
      const saveConfig = async () => {
        if (!form.appid) {
          ElMessage.warning('请填写AppID')
          return
        }
  
        submitting.value = true
        const loading = ElLoading.service({ text: '保存中...' })
  
        try {
          const saveData = { appid: form.appid, upload_key: form.upload_key }
          if (form.appsecret) saveData.appsecret = form.appsecret
  
          const response = await axios.post(`${config.BASE_URL}/shop/api/config_api.php`, {
            action: 'save',
            agent_id: agentId.value,
            store_id: storeId.value,
            config_key: 'miniprogram',
            config_value: JSON.stringify(saveData),
            config_group: 'miniprogram',
            description: '小程序配置'
          })
  
          if (response.data.code === 200) {
            ElMessage.success('保存成功')
            fetchConfig()
          } else {
            ElMessage.error(response.data.message || '保存失败')
          }
        } catch (err) {
          console.error('保存失败:', err)
          ElMessage.error('保存失败')
        } finally {
          submitting.value = false
          loading.close()
        }
      }
  
      const openUploadCodeDialog = () => {
        uploadForm.appid = form.appid
        uploadForm.privateKeyContent = form.upload_key
        uploadForm.default_store_id = storeId.value
        uploadForm.api_url = config.BASE_URL
        uploadLogs.value = []
        uploadProgress.value = 0
        uploadProgressStatus.value = ''
  
        if (!uploadForm.appid) {
          ElMessage.warning('请先配置小程序 AppID')
          return
        }
        if (!uploadForm.privateKeyContent) {
          ElMessage.warning('请先配置小程序上传密钥')
          return
        }
  
        uploadCodeDialogVisible.value = true
      }
  
      const submitUploadCode = async () => {
        if (!uploadForm.appid) { ElMessage.warning('请先配置小程序AppID'); return }
        if (!uploadForm.privateKeyContent) { ElMessage.warning('请先配置小程序上传密钥'); return }
        if (!uploadForm.api_url) { ElMessage.warning('API域名未配置'); return }
        if (!uploadForm.default_store_id) { ElMessage.warning('商家ID未配置'); return }
  
        uploading.value = true
        uploadProgress.value = 10
        uploadProgressStatus.value = ''
        uploadLogs.value = ['开始上传小程序代码...']
  
        try {
          const response = await axios.post(
            'https://db.juzhendi.com/auth/minidb/group_buy_mini_pro/upload.php',
            {
              appid: uploadForm.appid,
              privateKeyContent: uploadForm.privateKeyContent,
              api_url: uploadForm.api_url,
              default_store_id: String(uploadForm.default_store_id)
            },
            {
              timeout: 120000,
              onUploadProgress: (progressEvent) => {
                const percent = Math.min(90, 10 + Math.floor((progressEvent.loaded || 0) / (progressEvent.total || 1) * 80))
                uploadProgress.value = percent
              }
            }
          )
  
          uploadProgress.value = 100
  
          if (response.data.status === 'success') {
            uploadProgressStatus.value = 'success'
            uploadLogs.value.push('✅ 上传成功！')
            if (response.data.detail && Array.isArray(response.data.detail)) {
              response.data.detail.forEach(line => { if (line && line.trim()) uploadLogs.value.push(line) })
            }
            ElMessage.success('小程序代码上传成功')
          } else {
            uploadProgressStatus.value = 'exception'
            uploadLogs.value.push(`❌ 上传失败: ${response.data.message}`)
            ElMessage.error(response.data.message || '上传失败')
          }
        } catch (error) {
          console.error('上传失败:', error)
          uploadProgressStatus.value = 'exception'
          uploadLogs.value.push(`❌ 请求失败: ${error.message}`)
          if (error.response && error.response.data) {
            uploadLogs.value.push(`错误详情: ${JSON.stringify(error.response.data)}`)
          }
          ElMessage.error('上传失败，请检查网络或稍后重试')
        } finally {
          uploading.value = false
        }
      }
  
      onMounted(() => {
        fetchConfig()
      })
  
      return {
        form,
        formRef,
        submitting,
        uploading,
        uploadProgress,
        uploadProgressStatus,
        uploadLogs,
        uploadCodeDialogVisible,
        uploadForm,
        saveConfig,
        openUploadCodeDialog,
        submitUploadCode
      }
    }
  }
  </script>
  
  <style scoped lang="scss">
  .channel-config {
    .page-header {
      margin-bottom: 24px;
  
      .page-title {
        font-size: 24px;
        font-weight: 600;
        color: #1a1a2e;
        margin-bottom: 4px;
      }
  
      .page-desc {
        font-size: 14px;
        color: #8c8c8c;
      }
    }
  
    .config-container {
      width: 100%;
      max-width: 800px;
    }
  
    .config-card {
      border-radius: 16px;
      overflow: hidden;
    }
  
    .card-header-title {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 16px;
      font-weight: 500;
    }
  
    .form-tip {
      font-size: 12px;
      color: #999;
      margin-top: 4px;
      line-height: 1.4;
    }
  
    .save-btn-item {
      margin-top: 16px;
    }
  
    .btn-group {
      display: flex;
      gap: 16px;
    }
  
    .save-btn, .upload-btn {
      padding: 8px 24px;
      font-size: 14px;
      font-weight: 500;
    }
  
    .upload-progress {
      margin-top: 20px;
  
      .upload-log {
        margin-top: 12px;
        max-height: 300px;
        overflow-y: auto;
        background: #1e1e1e;
        border-radius: 8px;
        padding: 12px;
  
        .log-line {
          font-family: 'Monaco', 'Menlo', monospace;
          font-size: 12px;
          color: #d4d4d4;
          line-height: 1.5;
          white-space: pre-wrap;
          word-break: break-all;
        }
      }
    }
  }
  </style>